import React, {useEffect, useMemo, useRef, useState} from 'react'

type Props = {
  isListening: boolean
  volumeLevel?: number
  compact?: boolean
  className?: string
  isEntering?: boolean
  isExiting?: boolean
}

const AiListeningIndicator: React.FC<Props> = ({
  isListening,
  volumeLevel = 0,
  compact = false,
  className = '',
  isEntering = false,
  isExiting = false,
}) => {
  const [audioAmplitude, setAudioAmplitude] = useState(0)
  const audioReactiveScaleRef = useRef(1)
  const animationFrameRef = useRef<number | null>(null)
  const smoothedAmplitudeRef = useRef(0)

  useEffect(() => {
    if (!isListening) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      return
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      setAudioAmplitude(0)
      return
    }

    const updateAudioAmplitude = () => {
      if (typeof window !== 'undefined' && (window as any).audioAnalyserNode) {
        const analyser = (window as any).audioAnalyserNode as AnalyserNode
        const dataArray = new Uint8Array(analyser.frequencyBinCount)
        analyser.getByteFrequencyData(dataArray)

        let sum = 0
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i]
        }
        const rms = Math.sqrt(sum / dataArray.length) / 255
        const smoothingFactor = 0.15
        smoothedAmplitudeRef.current =
          smoothedAmplitudeRef.current * (1 - smoothingFactor) + rms * smoothingFactor

        const clampedAmplitude = Math.max(0.05, Math.min(1, smoothedAmplitudeRef.current))

        audioReactiveScaleRef.current = 1 + clampedAmplitude * 0.3

        setAudioAmplitude(clampedAmplitude)
      }

      animationFrameRef.current = requestAnimationFrame(updateAudioAmplitude)
    }

    animationFrameRef.current = requestAnimationFrame(updateAudioAmplitude)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [isListening])

  const reactiveVolumeLevel = useMemo(() => {
    return Math.max(volumeLevel, audioAmplitude)
  }, [volumeLevel, audioAmplitude])

  const volumeScale = useMemo(() => {
    return reactiveVolumeLevel > 0 ? 1 + reactiveVolumeLevel * 0.15 : 1
  }, [reactiveVolumeLevel])

  const scaleStyle = useMemo(
    () =>
      reactiveVolumeLevel > 0
        ? {transform: `translate(-50%, -50%) scale(${volumeScale})`}
        : undefined,
    [reactiveVolumeLevel, volumeScale]
  )

  const stateClass = useMemo(() => {
    if (isExiting) return 'exiting'
    if (isEntering) return 'entering'
    return ''
  }, [isEntering, isExiting])

  const volumeStateClass = useMemo(() => {
    if (reactiveVolumeLevel > 0.7) return 'volume-high'
    if (reactiveVolumeLevel > 0.4) return 'volume-medium'
    if (reactiveVolumeLevel > 0.1) return 'volume-low'
    return 'volume-silent'
  }, [reactiveVolumeLevel])

  if (!isListening) return null

  return (
    <div
      className={`ai-listening-indicator ${compact ? 'compact' : ''} ${stateClass} ${volumeStateClass} voice-volume-reactive ${className}`.trim()}
      style={scaleStyle}
      aria-hidden='true'
      data-state='listening'
      data-volume={Math.round(reactiveVolumeLevel * 100)}
    />
  )
}

export default React.memo(AiListeningIndicator)
